/**
 * @fileoverview Manual (body-only) placeholder discovery for letter templates.
 *
 * Purpose:
 * - Decide whether a placeholder key found in a template body is a plain
 *   per-document input the practitioner types by hand — as opposed to a key
 *   another subsystem resolves (contact bindings, firm references, collection
 *   items, the signature block).
 * - Produce the same human-readable fallback label the day-one Edit Fields
 *   panel uses for such keys, so a field reads identically before and after
 *   generation.
 *
 * Why: a placeholder inserted into a template body after extraction (e.g.
 * `{{accountants_initials}}` in an "Our ref:" line) has no canonical
 * definition, no org custom-field definition and no `extractedFields` row.
 * The day-one editor still surfaces it (it scans the body and falls back to
 * a title-cased label), but the generate wizard only registered contact keys
 * from the body scan and had no generic metadata fallback, so the input was
 * missing until the document had been generated — MOR-2314.
 *
 * Used by:
 * - `_layout.jobs.$jobId.generate-precedents.tsx` — field registration and
 *   per-document card metadata.
 */

import {
  isAnyFirmRefKey,
  isContactCustomFieldKey,
  isContactFieldKey,
  isContactSourceFieldKey,
} from "@cvx/jobs/precedents/contactFields";

/** Key of the signature block — never a typed input. */
const SIGNATURE_KEY = "signature";

/**
 * Collection repeat-block item keys resolve per item at render time
 * (`collectionsShared.ts`), never from the scalar field map.
 */
const COLLECTION_ITEM_PREFIX = "item_";

/**
 * True when `key` is a plain per-document placeholder that has no dedicated
 * resolver and therefore needs a manual input in the generate wizard.
 *
 * Excluded families (each has its own discovery / fill path):
 * - contact bindings (`contact_job_*`, `contact_firm_*`), contact-source
 *   fields and contact custom fields — filled from the picked contact;
 * - firm references (`firmref_*`) — filled by the firm-reference picker;
 * - collection items (`item_*`) — filled per repeat-block item;
 * - the signature block.
 *
 * Canonical and org custom keys are not excluded here: callers resolve those
 * first and only reach for this predicate when no definition matched.
 */
export function isManualPlaceholderKey(key: string): boolean {
  if (!key) return false;
  if (key === SIGNATURE_KEY) return false;
  if (key.startsWith(COLLECTION_ITEM_PREFIX)) return false;
  if (isContactFieldKey(key)) return false;
  if (isContactSourceFieldKey(key)) return false;
  if (isContactCustomFieldKey(key)) return false;
  if (isAnyFirmRefKey(key)) return false;
  return true;
}

/**
 * Title-case a snake_case placeholder key for display, e.g.
 * `accountants_initials` → "Accountants Initials". Mirrors the fallback the
 * day-one Edit Fields panel applies so the wizard input carries the same
 * label as the chip in the template preview.
 */
export function humanizeFieldKey(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Text-input metadata for a manual placeholder that has no canonical, custom
 * or template-extracted definition. Shape matches the wizard's
 * `CanonicalField` rows so it renders through the existing
 * `FieldReviewInput` path unchanged.
 */
export function manualFieldFallback(key: string): {
  key: string;
  label: string;
  fieldType: "text";
} {
  return { key, label: humanizeFieldKey(key), fieldType: "text" };
}
